import { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { ContainerRestauLight } from "../../../../components/containerRestau/containerRestauLight";
import { isTypedRestaurantFilter } from "../../../../components/types/restaurantTypes";
import { FilterValue, formatFilterLabel, getCuisineDisplayLabel, IndexablePage, listFiltersResto, Restaurant, TIME_FILTERS } from "../../../../components/types/types";
import { filterRestaurantsWithFilters, getScheduleFilteredList } from "../../../../context/RestaurantContext";

export const normalizeCity = (s?: string) =>(s || "").trim().toLowerCase();

interface HookListSortProps {
    currentList:string[],
    elem:[string,string[][]],
    handleClickSort: (values: string[]) => void;
    lockedGroups: Set<string>;
    onLockedClick: () => void;
    isSectionLocked?: boolean;
    availableSectionValues: string[];
}
export const HookListSort:React.FC<HookListSortProps> = ({currentList,elem,handleClickSort,lockedGroups,onLockedClick,isSectionLocked,availableSectionValues}) => {
    const sectionName = elem[0];
    const neighborhoodGroups = elem[1];
    const hasChildren = neighborhoodGroups.length > 0;

    const isSectionSelected = availableSectionValues.length > 0 && availableSectionValues.every((value) => currentList.includes(value) );
    const sectionReallyLocked = !isSectionSelected && isSectionLocked;

    return <>
        <span className={`elem ${isSectionSelected  ? "isSelect" : ""} ${sectionReallyLocked ? "isLocked" : 'pointer lowOpacityHover'} XsmallTextBold`}   onClick={() => {if (sectionReallyLocked) {onLockedClick();return;}handleClickSort(availableSectionValues)}}> 
                <div className="checkBox isLarge"></div>
                {sectionName}&nbsp;:
        </span>
        {hasChildren && (<>
                {neighborhoodGroups.map(group => {
                    const groupLabel = group.join(" / ");
                    const groupKey = group.join("||");
                    const isGroupSelected = group.length > 0 &&group.every((value) => currentList.includes(value));
                    const isLocked = !isGroupSelected && lockedGroups.has(groupKey);
                    return <span key={`${sectionName}-${group.join("-")}`} className={`elem elemChild ${isGroupSelected ? "isSelect" : ""} ${isLocked ? "isLocked" : 'pointer lowOpacityHover'} `} onClick={() => {if (isLocked) {onLockedClick();return}handleClickSort(group)}} ><div className="checkBox"></div>{groupLabel}</span>
                })}
            </>
        )}
    </>
}

export const MainCorpSkeleton = () => (
    <div className="mainCorpRestau mainCorpSkeleton">
        <div className="mainSkeletonHeader">
            <div className="mainSkeletonTitle" />
            <div className="mainSkeletonCount" />
        </div>

        {Array.from({ length: 9 }).map((_, index) => (
            <div className={`containerRestauUp mainSkeletonCard ${index % 3 !== 0 ? "notFirst" : ""}`} key={index}>
                <div className="containerRestau mainSkeletonInner">
                    <div className="imageZoneRestau mainSkeletonImage" />

                    <div className="separation" />

                    <div className="footerContainerRestau mainSkeletonFooter">
                        <div className="firstLine">
                            <div className="mainSkeletonLine restaurantName" />

                            <div className="mainSkeletonRate">
                                <div className="mainSkeletonLine note" />
                                <div className="mainSkeletonLine reviews" />
                            </div>
                        </div>

                        <div className="scndLine">
                            <div className="mainSkeletonLine restaurantType" />

                            <div className="mainSkeletonPrice">
                            </div>
                        </div>

                        <div className="thirdLine mainSkeletonLine">
                        </div>

                        <div className="fourthLine">
                            <div className="mainSkeletonLine description long" />
                            <div className="mainSkeletonLine description medium" />
                        </div>
                    </div>
                </div>
            </div>
        ))}
    </div>
);

export const buildPageTitle = (cityLoad?: string,seoPage?: IndexablePage | null,currentYear?: number): string | null => {
    if (seoPage?.label && seoPage.page_kind !== 'city')     return seoPage.label;
    if (cityLoad && cityLoad !== 'à proximité') {
        return `Trouvez un bon restaurant ${cityLoad}, Maine-et-Loire – ${currentYear}`;
    }
    if (cityLoad === 'à proximité') {
        return `Les bons restaurants à proximité`;
    }

    return null;
};
const CLASSIC_FILTER_PRIORITY = [
    "Parking",
    "Idéal pour",
    "Ambiance et expérience",
    "Options pratiques",
    "Moyen de paiement",
    "Régime alimentaire",
    "Accessibilités",
] as const;
const classicPriority = new Map(
    CLASSIC_FILTER_PRIORITY.flatMap((category, priority) => {
        const group = listFiltersResto.find( filter => filter.value === category);
        return (group?.liste ?? []).map(value => [value,priority] as const);
    })
);
const isCuisineCandidate = (filter: string,restaurants: Restaurant[]): boolean => {
    return restaurants.some((restaurant) => restaurant.cuisines?.includes(filter));
};
const orderExtendableFilters = (filters: string[],universe: Restaurant[]): string[] => {
    const classicBefore: string[] = [];
    const classicAfter: string[] = [];
    const cuisines: string[] = [];
    const restaurantTypes: string[] = [];
    for (const filter of filters) {
        if (isCuisineCandidate(filter, universe)) {
            cuisines.push(filter);
            continue;
        }
        if (isTypedRestaurantFilter(filter)) {
            restaurantTypes.push(filter);
            continue;
        }
        const priority = classicPriority.get(filter) ?? Number.MAX_SAFE_INTEGER;
        if (priority <= 4) {
            classicBefore.push(filter);
        } else {
            classicAfter.push(filter);
        }
    }
    classicBefore.sort((a, b) => {
        const priorityA = classicPriority.get(a) ?? Number.MAX_SAFE_INTEGER;
        const priorityB = classicPriority.get(b) ?? Number.MAX_SAFE_INTEGER;
        return priorityA - priorityB;
    });
    classicAfter.sort((a, b) => {
        const priorityA = classicPriority.get(a) ?? Number.MAX_SAFE_INTEGER;
        const priorityB = classicPriority.get(b) ?? Number.MAX_SAFE_INTEGER;
        return priorityA - priorityB;
    });
    return [...classicBefore,...cuisines.reverse(),...restaurantTypes.reverse(),...classicAfter];
};
const EMPTY_RESTO_LIST: Restaurant[] = [];
const EMPTY_ID_SET = new Set<number>();
export interface ExtendedFilterSection {
    filterLabel: string;
    restos: Restaurant[];
    canShowMore: boolean;

    replacementFilter?: FilterValue;
    isPriceSection?: boolean;
    isCuisineSection?: boolean;
}
const EMPTY_SECTIONS: ExtendedFilterSection[] = [];
const isExtendableCandidate = (filter: FilterValue): filter is string => typeof filter === "string" && !TIME_FILTERS.has(filter) && filter !== "À proximité";

export const buildExtendedFilterSections = (universe: Restaurant[],activeFilters: FilterValue[],extendableFilters: string[],existingIds: Set<number>): ExtendedFilterSection[] => {
    if (!universe.length) return EMPTY_SECTIONS;
    const orderedCandidates = orderExtendableFilters(extendableFilters,universe);
    const sections: ExtendedFilterSection[] = [];
    const usedIds = new Set<number>();
    for (const filterToRemove of orderedCandidates) {
        if (sections.length >= 3) break;
        const filtersWithout = activeFilters.filter((f) => f !== filterToRemove);
        const resultWithout = filterRestaurantsWithFilters(universe, filtersWithout);
        const newOnes = resultWithout.filter(
            (r) => !existingIds.has(r.idrestaurant) && !usedIds.has(r.idrestaurant)
        );

        if (newOnes.length >= 2) {
            const picked = newOnes.slice(0, 4);
            picked.forEach((r) => usedIds.add(r.idrestaurant));
            sections.push({ filterLabel: filterToRemove, restos: picked,canShowMore: newOnes.length > 4, isCuisineSection: isCuisineCandidate(filterToRemove, universe) });
        }
    }
    if (sections.length < 3) {
        const priceFilter = activeFilters.find((filter): filter is [number, number] => Array.isArray(filter));
        if (priceFilter) {
            const [minPrice, maxPrice] = priceFilter;
            const relaxedPrice: [number, number] = [Math.max(0, minPrice - 10),maxPrice + 10];
            const filtersWithRelaxedPrice = activeFilters.map((filter) => filter === priceFilter ? relaxedPrice : filter);
            const resultWithSimilarPrice = filterRestaurantsWithFilters(universe,filtersWithRelaxedPrice);
            const newOnes = resultWithSimilarPrice.filter((r) => !existingIds.has(r.idrestaurant) && !usedIds.has(r.idrestaurant));
            if (newOnes.length >= 2) {
                const picked = newOnes.slice(0, 4);
                picked.forEach((r) => usedIds.add(r.idrestaurant));
                sections.push({filterLabel: "Prix similaire",restos: picked,canShowMore: newOnes.length > 4,replacementFilter: relaxedPrice,isPriceSection: true});
            }
        }
    }

    return sections;
};

interface UseExtendedFilterSearchParams {
    listResto: Restaurant[];
    filterChoose: FilterValue[];
    dateSelected: Date | null;
    hourSelected: string;
    currentNeighborhood: string[];
    doSplit: boolean;
    targetCityNormalized: string;
    sortedList: Restaurant[];
    indexFirstAround: number;
    baseResultCount: number;
}

export const useExtendedFilterSearch = ({listResto,filterChoose,dateSelected,hourSelected,currentNeighborhood,doSplit,targetCityNormalized,sortedList,indexFirstAround,baseResultCount}: UseExtendedFilterSearchParams): ExtendedFilterSection[] => {
    const extendableFilters = useMemo(
        () => filterChoose.filter(isExtendableCandidate),
    [filterChoose]);
    const shouldTryExtendedFilterSearch = useMemo(() => {
        const hasDateOrHour = !!dateSelected || hourSelected !== "";
        const hasPriceFilter = filterChoose.some((filter) =>Array.isArray(filter));
        const relaxableFilterCount = extendableFilters.length + (hasPriceFilter ? 1 : 0);
        const hasEnoughFilters = relaxableFilterCount >= 2 || (relaxableFilterCount >= 1 && hasDateOrHour );
        return  hasEnoughFilters && baseResultCount <= 20
    }, [extendableFilters,filterChoose,dateSelected,hourSelected,baseResultCount]);
    const contextualList = useMemo(() => {
        if (!shouldTryExtendedFilterSearch) return EMPTY_RESTO_LIST;
        return getScheduleFilteredList(listResto,hourSelected,dateSelected);
    }, [shouldTryExtendedFilterSearch,listResto,hourSelected,dateSelected]);
    const contextualUniverse = useMemo(() => {
        if (!shouldTryExtendedFilterSearch || !contextualList.length) return EMPTY_RESTO_LIST;
        const neighborhoodFiltered = currentNeighborhood.length > 0 ? contextualList.filter((r) => currentNeighborhood.includes(r.neighborhood)) : contextualList;
        if (!doSplit) return neighborhoodFiltered;
        return neighborhoodFiltered.filter((r) => { const rCity = normalizeCity(r.address?.[1]); const rVisibleCity = normalizeCity(r.visiblecity);
            return (rCity === targetCityNormalized ||rVisibleCity === targetCityNormalized);
        });
    }, [shouldTryExtendedFilterSearch,contextualList,currentNeighborhood,doSplit,targetCityNormalized]);
    const existingResultIds = useMemo(() => {
        if (!shouldTryExtendedFilterSearch) return EMPTY_ID_SET;
        const scopedResults = doSplit ? sortedList.slice(0,indexFirstAround === -1 ? sortedList.length : indexFirstAround) : sortedList;
        return new Set(scopedResults.map((r) => r.idrestaurant));
    }, [shouldTryExtendedFilterSearch,doSplit,sortedList,indexFirstAround]);
    return useMemo(() => {
        if (!shouldTryExtendedFilterSearch ||!contextualUniverse.length) return EMPTY_SECTIONS;
        return buildExtendedFilterSections(contextualUniverse,filterChoose,extendableFilters,existingResultIds);
    }, [shouldTryExtendedFilterSearch,contextualUniverse,filterChoose,extendableFilters,existingResultIds]);
}
interface ExtendedFilterSectionsProps {
    extendedFilterSections: ExtendedFilterSection[];
    onShowAll: (section: ExtendedFilterSection) => void;
}
export const RenderExtendedFilterSections: React.FC<ExtendedFilterSectionsProps> = ({extendedFilterSections,onShowAll}) => {
    const listRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [scrollStates, setScrollStates] = useState<Record<string, { canScrollLeft: boolean; canScrollRight: boolean }>>({});
    const rafRef = useRef<Record<string, number | null>>({});

    const updateScrollState = (sectionId: string) => {
        if (rafRef.current[sectionId]) return;
        rafRef.current[sectionId] =
            requestAnimationFrame(() => {
                rafRef.current[sectionId] = null;
                const elem = listRefs.current[sectionId];
                if (!elem) return;
                const canScrollLeft = elem.scrollLeft > 5;
                const canScrollRight = elem.scrollLeft + elem.clientWidth < elem.scrollWidth - 5;

                setScrollStates((prev) => {
                    const previous = prev[sectionId];
                    if (previous?.canScrollLeft === canScrollLeft && previous?.canScrollRight === canScrollRight) return prev;

                    return {...prev,[sectionId]: {canScrollLeft,canScrollRight}};
                });
            });
    };

    const scrollSection = (sectionId: string,direction: "left" | "right") => {
        const elem = listRefs.current[sectionId];
        if (!elem) return;
        const scrollAmount = elem.clientWidth * 0.8;
        elem.scrollBy({left: direction === "right" ? scrollAmount : -scrollAmount,behavior: "smooth"});
    };
    useEffect(() => {
        const updateAll = () => {
            extendedFilterSections.forEach(
                (section) => {updateScrollState( section.filterLabel );}
            );
        };
        const raf =requestAnimationFrame(updateAll);
        window.addEventListener("resize",updateAll);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("resize",updateAll);
        };
    }, [extendedFilterSections]);

    useEffect(() => {
        return () => {
            Object.values(rafRef.current
            ).forEach((id) => {
                if (id) cancelAnimationFrame(id);
            });
        };
    }, []);

    return <div className="extendedFilterSections">
        {extendedFilterSections.map((section,idSection) => {
            const sectionId = section.filterLabel;
            const labels = getCuisineDisplayLabel(formatFilterLabel(section.filterLabel));
            const displayedLabel = section.isCuisineSection ? `Cuisine ${labels}` : labels;
            return <><div className="extendedFilterSection" key={`extended-${section.filterLabel}`}>
                <h3 className="largerText">
                    {section.isPriceSection ? <span><strong>Avec un prix similaire&nbsp;:</strong></span> : <><span className={`textPhone ${displayedLabel.length <= 14 ? 'sameLine' : ''}`}>Sans le filtre&nbsp;: <strong>“{displayedLabel}”</strong></span> <span className="textComputer">Sans le filtre&nbsp;: <strong>“{displayedLabel}”</strong></span></>}
                </h3>

                <div className="listWrapper">
                    <div className="shadowLeftSection"></div>
                    <div className="shadowRightSection"></div>
                    {scrollStates[sectionId]?.canScrollLeft && (
                        <div className="homeSectionScrollButton left" onClick={() => scrollSection(sectionId, "left")}><FiChevronLeft size={22} /></div>
                    )}
                    <div className="list" ref={(elem) => {listRefs.current[sectionId] = elem}} onScroll={() => updateScrollState(sectionId)}>
                        {section.restos.map((elem, index) => (
                            <ContainerRestauLight key={`extended-${section.filterLabel}-${elem.idrestaurant}`} resto={elem} isHighlighted={false} priority={false} eager={index<2}/>
                
                        ))}
                        {section.canShowMore && <div className="containerRedirectBloc flexAllCenter" ><div className="redirectBloc XsmallText pointer lowOpacityHover smallWhiteRoundButton" onClick={() => onShowAll(section)}>Tout afficher <div className="flexAllCenter"><FiChevronRight className="iconFirst"/></div></div> </div>}
                    </div>
                    {scrollStates[sectionId]?.canScrollRight && (
                        <div className="homeSectionScrollButton right"  onClick={() => scrollSection(sectionId, "right")}><FiChevronRight size={22} /> </div>
                    )}
                    
                </div>
            </div>
            {idSection < extendedFilterSections.length - 1 && <div className="separationExtendedSection"></div>}
            </>
        })}
    </div>
};